import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LiveCallModelsService } from '../../live-call-models.service';
import { LiveCallModel } from '../../live-call-models.models';

export type ModelPanelMode = 'create' | 'edit';

/**
 * Add or edit one voice model of the live-call catalog: provider, the exact
 * model code sent to the provider's API, a display name, its voice modality,
 * and a free-form note.
 *
 * The catalog default ("in use") is NOT set here — it is transferred from the
 * table's row action so it stays a deliberate, single-click decision.
 */
@Component({
  selector: 'app-model-form-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './model-form-panel.component.html',
})
export class ModelFormPanelComponent implements OnChanges {
  @Input() mode: ModelPanelMode = 'create';
  @Input() model: LiveCallModel | null = null;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private modelsService = inject(LiveCallModelsService);

  submitting = signal(false);
  errorMessage = signal('');

  readonly providers = [
    { value: 'google', label: 'Gemini (Google)' },
    { value: 'openai', label: 'OpenAI' },
  ];
  readonly modalities = ['native-audio', 'half-cascade', 'realtime'];

  form = {
    provider: 'google',
    model_code: '',
    display_name: '',
    modality: 'native-audio',
    notes: '',
    is_active: true,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] || changes['mode']) {
      this.errorMessage.set('');
      if (this.mode === 'edit' && this.model) {
        this.form = {
          provider: this.model.provider,
          model_code: this.model.modelCode,
          display_name: this.model.displayName,
          modality: this.model.modality,
          notes: this.model.notes ?? '',
          is_active: this.model.isActive ?? true,
        };
      } else {
        this.form = {
          provider: 'google',
          model_code: '',
          display_name: '',
          modality: 'native-audio',
          notes: '',
          is_active: true,
        };
      }
    }
  }

  onSubmit(): void {
    this.errorMessage.set('');
    if (!this.form.model_code.trim()) return this.errorMessage.set('Model code is required.');
    if (!this.form.display_name.trim()) return this.errorMessage.set('Display name is required.');

    this.submitting.set(true);
    const done = {
      next: () => {
        this.submitting.set(false);
        this.saved.emit();
      },
      error: (err: any) => {
        this.submitting.set(false);
        this.errorMessage.set(err?.message ?? 'Save failed.');
      },
    };

    const payload = {
      provider: this.form.provider,
      model_code: this.form.model_code.trim(),
      display_name: this.form.display_name.trim(),
      modality: this.form.modality,
      notes: this.form.notes.trim() || null,
      is_active: this.form.is_active,
    };

    if (this.mode === 'create') {
      this.modelsService.createModel(payload).subscribe(done);
      return;
    }
    this.modelsService.patchModel(this.model!.id, payload).subscribe(done);
  }

  onClose(): void {
    this.closed.emit();
  }
}
